import AppIcon from "common/app-icon";
import { defined } from "common/Support";
import { ErrorBoundary } from "lib/error-boundary";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import * as React from "react";
import { Suspense } from "react";
import type { DateTimeVariant } from "webapp/renderers/base-date-time-field-material-renderer";
import type { FormElementProps } from "webapp/ui/views/form-element/form-element";
import { BasePicker } from "webapp/ui/views/base-picker/base-picker";
import { dateClearedTime, dateNoSeconds } from "./private/date-time-picker-utils";
import { PastAndOrFuture } from "common/components/past-future";
import type { AppKind } from "common/components/app-kind";

const MaterialDateTimePicker = lazilyLoadedComponent(
    "material-date-time-picker",
    () => import("./private/material-date-time-picker")
);

interface DateTimeProps extends FormElementProps {
    readonly variant: DateTimeVariant;
    readonly placeholder: string;
    readonly value: Date | undefined;
    readonly pastAndOrFuture: PastAndOrFuture;
    readonly appKind: AppKind;
    readonly onChange: (value: Date | undefined) => void;
}

type Props = DateTimeProps;

interface State {
    showPicker: boolean;
    pickerValue: Date | undefined;
}

class DateTimePicker extends React.PureComponent<Props, State> {
    public state: State = {
        showPicker: false,
        pickerValue: undefined,
    };

    private showPicker = (): void => {
        const { variant, value } = this.props;

        if (value === undefined) {
            const defaultDateValue = variant === "date" ? dateClearedTime(new Date()) : dateNoSeconds(new Date());
            this.setState({ pickerValue: defaultDateValue, showPicker: true });
        } else {
            this.setState({ pickerValue: value, showPicker: true });
        }
    };

    private hidePicker = (): void => {
        this.setState({ showPicker: false });
    };

    private onClear = (ev: React.MouseEvent<HTMLElement>): void => {
        const { onChange } = this.props;

        onChange?.(undefined);

        ev.stopPropagation();
    };

    private onUpdate = (dateTime: Date | undefined): void => {
        this.setState({ pickerValue: dateTime });
    };

    private onCancel = (): void => {
        this.hidePicker();
    };

    private onAccept = (): void => {
        const { onChange } = this.props;
        const { pickerValue } = this.state;

        if (onChange === undefined || pickerValue === null) return;

        onChange(pickerValue);

        this.hidePicker();
    };

    render() {
        const { variant, title, placeholder, value, pastAndOrFuture, isEnabled, isRequired, appKind } = this.props;
        const { showPicker, pickerValue } = this.state;

        const hasValue = value !== undefined;

        let valueString = placeholder;
        if (variant === "date" && hasValue) {
            valueString = defined(value).toLocaleDateString();
        } else if (variant === "date-time" && hasValue) {
            valueString = `${defined(value).toLocaleDateString()} ${defined(value).toLocaleTimeString()}`;
        }

        return (
            <>
                <BasePicker
                    title={title}
                    isEnabled={isEnabled}
                    isRequired={isRequired}
                    onClick={this.showPicker}
                    leftItem={<AppIcon icon="01-21-calendar" size={24} />}
                    rightItem={hasValue ? "clear" : undefined}
                    rightItemOnClick={this.onClear}
                    appKind={appKind}>
                    {valueString}
                </BasePicker>
                {showPicker && pickerValue !== undefined && (
                    <ErrorBoundary>
                        <Suspense fallback={<></>}>
                            <MaterialDateTimePicker
                                type={variant}
                                value={pickerValue}
                                disablePast={pastAndOrFuture === PastAndOrFuture.TodayOrFuture}
                                disableFuture={pastAndOrFuture === PastAndOrFuture.TodayOrPast}
                                onUpdate={this.onUpdate}
                                onCancel={this.onCancel}
                                onAccept={this.onAccept}
                                appKind={appKind}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )}
            </>
        );
    }
}

export default DateTimePicker;
